<template>
  <AdminLayout>
    <div v-if="!$store.state.general.loadingSpinner" class="flex flex-col">
      <DataTable
        :columns="dtColumns"
        :rows="rows"
        :clickFn="viewBudgetRequest"
        :sort="sort"
        @sort-updated="handleNewSort"
      >
        <template #filters="slotProps">
          <div class="flex justify-start items-center mb-4">
            <BudgetRequestsFilters :filters="filters" @update-filter="handleNewFilter" />
          </div>
        </template>
        <template #td-company="slotProps">
          <div class="flex items-center">
            <div>
              <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                {{ slotProps.row.company }}
              </div>
              <div class="text-sm leading-5 text-gray-500 truncate">
                {{ slotProps.row.email_reply_to }}
              </div>
            </div>
          </div>
        </template>

        <template #td-setup_progress="slotProps">
          <RadialProgress style="--size: 2rem; font-size: 0.5rem" :progress="slotProps.row.setup_progress" />
        </template>

        <template #td-status="slotProps">
          <span
            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
            :class="
              slotProps.row.status == `pending`
                ? `bg-yellow-100 text-yellow-800`
                : slotProps.row.status == `completed`
                  ? `bg-green-100 text-green-800`
                  : `bg-red-100 text-red-800`
            "
          >
            {{ slotProps.row.status }}
          </span>
        </template>
      </DataTable>
    </div>

    <LoadingList />

    <Pagination :startEntry="startEntry" :endEntry="endEntry" :total="total" />
  </AdminLayout>
</template>

<script>
import AdminLayout from '../AdminLayout/index.vue';
import LoadingList from '../shared/LoadingList.vue';
import DataTable from '../shared/DataTable/index.vue';
import BudgetRequestsFilters from './BudgetRequestsFilters.vue';
import { formatCurrencyToHuman, formatStandardDateTime } from '../../utilities/format.js';
import RadialProgress from '../shared/RadialProgress.vue';
import Pagination from '../shared/Pagination.vue';

export default {
  components: {
    AdminLayout,
    DataTable,
    LoadingList,
    BudgetRequestsFilters,
    RadialProgress,
    Pagination,
  },
  async mounted() {
    await this.getTeams();
  },
  data() {
    return {
      teams: [],
      loaded: false,
      startEntry: 0,
      endEntry: 0,
      total: 0,
      page: this.$route.query.page ? this.$route.query.page : 1,
    };
  },
  methods: {
    async viewBudgetRequest(record) {
      await this.$router.push({ name: 'Budget Request Admin', params: { id: record.id } });
    },
    async getTeams() {
      this.$store.commit('general/showLoadingSpinner');
      const request = await fetch(
        `${this.$route.fullPath.replace(
          '/admin/budget_requests',
          '/api/admin/monthly_marketing_budget_update_requests'
        )}`
      );
      const data = await request.json();
      this.$store.commit('general/hideLoadingSpinner');
      if (data.records && Array.isArray(data.records)) {
        for (let record of data.records) {
          record.company = record.team?.company || '';
        }
        this.teams = data.records;
      } else {
        this.teams = [];
      }
      this.startEntry = data.start_entry;
      this.endEntry = data.end_entry;
      this.total = data.total;
      this.loaded = true;
    },
    async handleNewSort(sort) {
      await this.$router.push({
        path: '/admin/budget_requests',
        query: { sort: sort.key.toLowerCase(), order: sort.order, status: this.$route.query.status },
      });
      await this.getTeams();
    },
    async handleNewFilter(newFilters, e) {
      const query = {
        sort: 'created',
        order: 'desc',
        status: newFilters.status.value || 'All',
      };
      await this.$router.push({ path: '/admin/budget_requests', query });
      await this.getTeams();
    },
  },
  computed: {
    dtColumns() {
      return [
        {
          label: 'Team',
          key: 'company',
          sortable: true,
        },
        {
          label: 'Total Spend',
          key: 'total_spend',
          formatter: formatCurrencyToHuman,
          sortable: true,
        },
        {
          label: 'Reason',
          key: 'reason',
          sortable: true,
        },
        {
          label: 'Status',
          key: 'status',
          sortable: true,
        },
        {
          label: 'Created',
          key: 'created_at',
          formatter: formatStandardDateTime,
          sortable: true,
        },
      ];
    },
    rows() {
      return this.teams;
    },
    columns() {
      if (this.teams.length > 0) {
        let columns = Object.keys(this.teams[0]).filter(column => column != 'id');
        return columns;
      } else {
        return [];
      }
    },
    sort() {
      return {
        key: this.$route.query.sort,
        order: this.$route.query.order,
      };
    },
    filters() {
      const query = this.$route.query;
      let filters = [];
      filters.push({ key: 'status', value: query.status || 'All' });
      return filters;
    },
  },
};
</script>
